import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation, Outlet } from 'react-router-dom';
import { Users, LogOut, Menu, X, ChevronRight } from 'lucide-react';

/* ------------------------------------------------------------------ */
/*  导航菜单项定义                                                      */
/* ------------------------------------------------------------------ */

interface NavItem {
  path: string;
  label: string;
  icon: string;
}

const NAV_ITEMS: NavItem[] = [
  { path: '/admin/dashboard', label: '数据概览', icon: '\uD83D\uDCCA' },
  { path: '/admin/contacts', label: '客户管理', icon: '\uD83D\uDC65' },
  { path: '/admin/batches', label: '导入记录', icon: '\uD83D\uDCE5' },
  { path: '/admin/dealers', label: '经销商管理', icon: '\uD83D\uDFE2' },
  { path: '/admin/settings', label: '系统设置', icon: '\u2699\uFE0F' },
];

/* ------------------------------------------------------------------ */
/*  获取当前页面标题                                                    */
/* ------------------------------------------------------------------ */

function getPageTitle(pathname: string): string {
  const item = NAV_ITEMS.find((n) => n.path === pathname);
  return item?.label || '管理后台';
}

/* ------------------------------------------------------------------ */
/*  AdminLayout 组件                                                   */
/* ------------------------------------------------------------------ */

const AdminLayout: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [username, setUsername] = useState('');

  /* ---- 检查登录状态 ---- */
  useEffect(() => {
    const token = localStorage.getItem('admin_token');
    const storedUsername = localStorage.getItem('admin_username');
    if (!token) {
      navigate('/admin/login', { replace: true });
      return;
    }
    setUsername(storedUsername || 'admin');
  }, [navigate]);

  /* ---- 路由变化时关闭移动端侧边栏 ---- */
  useEffect(() => {
    setSidebarOpen(false);
  }, [location.pathname]);

  /* ---- 退出登录 ---- */
  const handleLogout = () => {
    localStorage.removeItem('admin_token');
    localStorage.removeItem('admin_username');
    navigate('/admin/login', { replace: true });
  };

  /* ---- 侧边栏内容 ---- */
  const sidebarContent = (
    <div className="flex flex-col h-full">
      {/* Logo 区域 */}
      <div className="flex items-center gap-3 px-5 py-5 border-b border-slate-700/50">
        <div className="w-9 h-9 rounded-lg bg-gradient-to-br from-blue-500 to-indigo-500 flex items-center justify-center shrink-0">
          <Users className="w-5 h-5 text-white" />
        </div>
        <div className="min-w-0">
          <h2 className="text-base font-bold text-white truncate">ContactConverter</h2>
          <p className="text-xs text-slate-400 truncate">管理后台</p>
        </div>
      </div>

      {/* 导航菜单 */}
      <nav className="flex-1 px-3 py-4 space-y-1 overflow-y-auto">
        {NAV_ITEMS.map((item) => {
          const isActive = location.pathname === item.path;
          return (
            <button
              key={item.path}
              onClick={() => navigate(item.path)}
              className={`
                w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium
                transition-all duration-150 text-left group
                ${
                  isActive
                    ? 'bg-blue-600/20 text-blue-400'
                    : 'text-slate-300 hover:bg-slate-700/50 hover:text-white'
                }
              `}
            >
              <span className="text-base shrink-0">{item.icon}</span>
              <span className="flex-1 truncate">{item.label}</span>
              {isActive && (
                <ChevronRight className="w-4 h-4 text-blue-400 shrink-0" />
              )}
            </button>
          );
        })}
      </nav>

      {/* 底部退出登录 */}
      <div className="px-3 py-4 border-t border-slate-700/50">
        <button
          onClick={handleLogout}
          className="w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium text-slate-400 hover:bg-red-500/10 hover:text-red-400 transition-all duration-150"
        >
          <LogOut className="w-4 h-4 shrink-0" />
          <span>退出登录</span>
        </button>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-slate-50 flex">
      {/* ========== 桌面端侧边栏 ========== */}
      <aside className="hidden lg:flex lg:fixed lg:inset-y-0 lg:z-30 lg:flex-col w-[240px] bg-[#1e293b]">
        {sidebarContent}
      </aside>

      {/* ========== 移动端侧边栏遮罩 ========== */}
      {sidebarOpen && (
        <div
          className="fixed inset-0 z-40 bg-black/50 lg:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* ========== 移动端侧边栏 ========== */}
      <aside
        className={`
          fixed inset-y-0 left-0 z-50 w-[260px] bg-[#1e293b] transform transition-transform duration-200 ease-out lg:hidden
          ${sidebarOpen ? 'translate-x-0' : '-translate-x-full'}
        `}
      >
        {sidebarContent}
      </aside>

      {/* ========== 主内容区 ========== */}
      <div className="flex-1 lg:pl-[240px] flex flex-col min-h-screen">
        {/* 顶部栏 */}
        <header className="sticky top-0 z-20 bg-white/80 backdrop-blur-sm border-b border-slate-200">
          <div className="flex items-center justify-between h-14 px-4 sm:px-6">
            {/* 左侧：汉堡菜单 + 页面标题 */}
            <div className="flex items-center gap-3">
              <button
                onClick={() => setSidebarOpen(true)}
                className="lg:hidden p-1.5 rounded-lg text-slate-500 hover:bg-slate-100 hover:text-slate-700 transition-colors"
              >
                <Menu className="w-5 h-5" />
              </button>
              <h1 className="text-base font-semibold text-slate-800">
                {getPageTitle(location.pathname)}
              </h1>
            </div>

            {/* 右侧：用户信息 */}
            <div className="flex items-center gap-2">
              <div className="w-8 h-8 rounded-full bg-gradient-to-br from-blue-500 to-indigo-500 flex items-center justify-center">
                <span className="text-xs font-bold text-white">
                  {username.charAt(0).toUpperCase()}
                </span>
              </div>
              <span className="hidden sm:inline text-sm text-slate-600 font-medium">
                {username}
              </span>
            </div>
          </div>
        </header>

        {/* 内容区 - 使用 Outlet 渲染子路由 */}
        <main className="flex-1 p-4 sm:p-6 lg:p-8"><Outlet /></main>
      </div>
    </div>
  );
};

export default AdminLayout;
